/** Écran 2 — Financement : besoin, curseurs de recettes, rabot, « affiché vs après réaction », compte de la 1re année. */
import { ledgerHtml } from "../components/ledger.js";
import { figureNotes, srcButton } from "../components/source-info.js";
import { STATUS_LABELS } from "../content-types.js";
import { missionCutGroups, type FinancingPreview, type MissionCutGroup } from "../engine-adapter.js";
import type { UiFinancing } from "../content-types.js";
import { cutPol, leverPol, missionCutPol, polPill } from "../political.js";
import { bn, escapeHtml as e, eur, int, missionShort, num, pct, signed } from "../format.js";
import type { Store } from "../state.js";
import { priceLine } from "../components/price-effects.js";
import { stepsHtml } from "./existe.js";
import { majorityThreshold } from "../components/tangible.js";
import { pastille, plate } from "../components/brand.js";

/** Effets prix du programme + financement (si le moteur les fournit). */
export function priceHtml(store: Store): string {
  if (!store.engine.caps.priceEffects) return "";
  try {
    const pe = store.engine.priceEffects(store.ctx, store.enginePrg, store.state.financing);
    return priceLine(store, pe) || `<p class="sm mut">Effet sur les prix : aucun effet notable.</p>`;
  } catch (err) {
    return `<p class="hint warn">Effet sur les prix indisponible : ${e(String(err))}</p>`;
  }
}

export function preview(store: Store): FinancingPreview {
  const s = store.state;
  return store.engine.previewFinancing(store.ctx, store.enginePrg, s.financing, s.seed);
}

export function gapHtml(store: Store, p: FinancingPreview): string {
  const covered = Math.min(p.raised_after_reaction_bn, p.gap_bn);
  const pctCov = p.gap_bn > 0 ? (covered / p.gap_bn) * 100 : 100;
  const bal0 = store.content.baseline.state.balance?.value;
  const trendTxt = bal0 !== null && bal0 !== undefined ? `, <b>en plus</b> du déficit tendanciel de l'État (≈ ${bn(Math.abs(bal0), 0)}${srcButton({ title: "Solde de l'État (année de base)", sources: [store.content.baseline.state.balance!.source], notes: figureNotes(store.content.baseline.state.balance) }, { inline: true })})` : "";
  const pop = store.content.baseline.macro.population;
  const perHead =
    pop && pop.value && p.gap_bn !== 0
      ? ` <span class="sm">soit ≈ ${eur(Math.round((Math.abs(p.gap_bn) * 1e9) / pop.value))} par habitant et par an${srcButton({ title: "Population", sources: [pop.source], notes: figureNotes(pop) }, { inline: true })}</span>`
      : "";
  // Plaque-chiffre de l'écran (une seule) : le montant à financer, à l'encre sur le jaune (un montant n'a pas de couleur d'effet).
  const amount = `${p.gap_bn > 0 ? "−" : p.gap_bn < 0 ? "+" : ""}${bn(Math.abs(p.gap_bn))}<small> /an</small>`;
  const missing = Math.max(0, p.debt_residual_bn);
  return `${plate("Effet de tes mesures sur le solde public, chaque année", amount, { note: perHead })}
  ${p.gap_bn !== 0 ? `<p class="sm">${p.gap_bn > 0 ? "« − » : tes mesures coûtent plus qu'elles ne rapportent" : "« + » : tes mesures rapportent plus qu'elles ne coûtent"}, en régime de croisière${trendTxt}.${p.gap_bn > 0 ? ` ${missing >= 0.05 ? `Il te manque <b>${bn(missing)}</b> : impôts, économies ou dette, à toi de choisir.` : "Tout est financé."} Ce que tu ne finances pas part en dette.` : ""}</p>` : ""}
  ${
    p.gap_bn > 0
      ? `<div class="bar" role="img" aria-label="Part financée par recettes et économies : ${pct(pctCov, 0)} ; reste en dette"><i class="rev" style="width:${Math.min(100, pctCov).toFixed(1)}%"></i><i class="debt" style="width:${Math.max(0, 100 - pctCov).toFixed(1)}%"></i></div>
         <p class="sm lgd"><span><i class="sw rev" aria-hidden="true"></i>Recettes et économies ${bn(p.raised_after_reaction_bn)}</span> <span><i class="sw debt" aria-hidden="true"></i>Dette ${bn(p.debt_residual_bn)}</span>
         ${p.raised_static_bn > p.raised_after_reaction_bn + 0.3 ? `<br><small>Affiché ${bn(p.raised_static_bn)}, après fuite et évitement ${bn(p.raised_after_reaction_bn)}.</small>` : ""}</p>`
      : p.gap_bn < 0
        ? `<p class="sm">Tes mesures rapportent plus qu'elles ne coûtent : le surplus réduit le déficit.</p>`
        : `<p class="sm">Aucune mesure à financer : les curseurs ci-dessous changent directement le déficit.</p>`
  }`;
}

/**
 * « Affiché » vs « après réaction ». Flèches et couleurs cohérentes sur les deux lignes : ▲ vert = la réaction améliore
 * le chiffre affiché, ▼ rouge = elle l'aggrave (déficit : solde plus négatif ; dette : ratio plus haut).
 */
export function cmpHtml(p: FinancingPreview): string {
  const cmpCell = (label: string, shownLabel: string, shown: number, simulated: number, worseIfHigher: boolean, dec: number, abs: boolean) => {
    const d = simulated - shown;
    const eps = Math.pow(10, -dec) / 2;
    const worse = worseIfHigher ? d > eps : d < -eps;
    const better = worseIfHigher ? d < -eps : d > eps;
    const tag = worse ? "pire que l'affiché" : better ? "mieux que l'affiché" : "comme l'affiché";
    const f = (x: number) => pct(abs ? Math.abs(x) : x, dec);
    // Flèche = sens de la variation du chiffre affiché (déficit en valeur absolue) ; couleur = effet sur le pays.
    const shownV = abs ? Math.abs(shown) : shown;
    const simV = abs ? Math.abs(simulated) : simulated;
    const pa = pastille(worse ? "worse" : better ? "better" : "equal", simV - shownV, signed(simV - shownV, dec).concat(" pt"), "face à l'affiché");
    return `<div><div class="l">${shownLabel}</div><div class="v">${f(shown)}</div></div>
      <div><div class="l">${label} après réaction</div><div class="v">${f(simulated)}</div><div class="l">${pa} ${tag}</div></div>`;
  };
  const debtNote =
    p.debt_static_pct > p.debt_simulated_pct + 0.5
      ? `<p class="hint">La dette « affichée » garde le PIB de départ : la croissance des prix et de l'activité fait mécaniquement baisser le ratio simulé, même quand le déficit se creuse.</p>`
      : "";
  return `<div class="cmp">
    ${cmpCell("Déficit", "Déficit affiché", p.deficit_static_pct, p.deficit_simulated_pct, false, 1, true)}
    ${cmpCell("Dette", "Dette affichée", p.debt_static_pct, p.debt_simulated_pct, true, 0, false)}
  </div>${debtNote}
  <p class="hint">Première année simulée : croissance ${p.first_year.kpis.growth_pct !== undefined ? pct(p.first_year.kpis.growth_pct) : "—"} · intérêts ${p.first_year.kpis.interest_bn !== undefined ? bn(p.first_year.kpis.interest_bn, 0) : "—"} · confiance ${p.first_year.kpis.confidence !== undefined ? int(p.first_year.kpis.confidence) : "—"} · coût politique du financement <b>${int(p.political_cost_per_year)}</b>/an.</p>`;
}

/** Coût politique du montant courant d'un levier (même unité partout, ADR 0011). */
export function leverPolHtml(store: Store, id: string): string {
  const v = store.state.financing.revenue_raises[id] ?? 0;
  return polPill(leverPol(store.ctx.params, id, v), { ratio: true });
}

export function rabotPolHtml(store: Store, bnPerPt: number): string {
  return polPill(cutPol(store.ctx.params, store.state.financing.across_the_board_cut, bnPerPt), { ratio: true });
}

/** Simulation sans crise (aperçu des économies par mission : effet propre, hors aléas). */
function quietRun(store: Store, fin: UiFinancing) {
  const ctx = { ...store.ctx, events: [] };
  return store.engine.simulate(ctx, store.enginePrg, fin, {}, store.state.seed, store.mode);
}

const previewCache = new Map<string, { saved: number | undefined; lines: string[]; year: number | undefined }>();

/**
 * Aperçu d'une économie ciblée : Md€ économisés la 1re année et indices de service touchés en fin de mandat, avec et sans
 * cette économie (« Hôpital : besoins couverts 96 % → 91 % »). Tout est calculé par le moteur ; missions touchées =
 * celles dont l'indice change (ou celles déclarées par le param du groupe).
 */
export function missionPreview(store: Store, g: MissionCutGroup): { saved: number | undefined; lines: string[]; year: number | undefined } {
  const fin = store.state.financing;
  const pctCut = fin.mission_cuts?.[g.id] ?? 0;
  const key = JSON.stringify([g.id, fin, store.enginePrg, store.state.seed, store.state.paramOverrides]);
  const hit = previewCache.get(key);
  if (hit) return hit;
  const without: UiFinancing = { ...fin, mission_cuts: { ...(fin.mission_cuts ?? {}) } };
  delete without.mission_cuts![g.id];
  const withCut: UiFinancing = { ...fin, mission_cuts: { ...(fin.mission_cuts ?? {}), [g.id]: pctCut || g.max } };
  let out: { saved: number | undefined; lines: string[]; year: number | undefined } = { saved: undefined, lines: [], year: undefined };
  try {
    const a = quietRun(store, without);
    const b = quietRun(store, withCut);
    const a0 = a.years[0];
    const b0 = b.years[0];
    const aL = a.years[a.years.length - 1];
    const bL = b.years[b.years.length - 1];
    const saved = a0 && b0 ? b0.account.balance_state - a0.account.balance_state : undefined;
    const ids = g.missions ?? Object.keys(bL?.service_index ?? {}).filter((id) => Math.abs((bL?.service_index[id] ?? 0) - (aL?.service_index[id] ?? 0)) > 1e-4);
    const lines = ids
      .filter((id) => aL?.service_index[id] !== undefined && bL?.service_index[id] !== undefined)
      .map((id) => {
        const label = missionShort(store.content.baseline.state.missions.find((m) => m.id === id)?.label ?? id);
        return `${e(label)} : besoins couverts ${int(aL!.service_index[id]! * 100)} % → <b>${int(bL!.service_index[id]! * 100)} %</b>`;
      });
    out = { saved: pctCut ? saved : undefined, lines, year: bL?.year };
  } catch {
    /* moteur qui ne lit pas mission_cuts : pas d'aperçu */
  }
  if (previewCache.size > 200) previewCache.clear();
  previewCache.set(key, out);
  return out;
}

export function missionPreviewHtml(store: Store, g: MissionCutGroup): string {
  const pctCut = store.state.financing.mission_cuts?.[g.id] ?? 0;
  const p = missionPreview(store, g);
  const pol = polPill(missionCutPol(g.polPerPt, pctCut, p.saved), { ratio: true });
  const lines = p.lines.length ? `${pctCut ? "" : `Au plafond (${pct(g.max, 0)}), en ${p.year ?? "fin de mandat"} : `}${p.lines.join(" · ")}` : "";
  return `<span class="xs">${pctCut && p.saved !== undefined ? `≈ ${bn(Math.abs(p.saved))} économisés la 1re année · ` : ""}${pol}</span>${lines ? `<br><span class="xs ${pctCut ? "" : "mut"}">${pctCut && p.year ? `En ${p.year} : ` : ""}${lines}</span>` : ""}`;
}

/** Bloc « Économies ciblées par mission » (contrat v0.3 supposé, détecté : params `cut_<groupe>_max_pct` + moteur qui lit mission_cuts). */
function missionCutsBlock(store: Store): string {
  const groups = missionCutGroups(store.content.params);
  if (!groups.length) return `<h3>Économies ciblées par mission</h3><p class="hint">Arrivent avec le moteur v0.3 (plafonds par groupe de missions à publier dans les paramètres).</p>`;
  const off = !store.engine.caps.missionCuts;
  const rows = groups
    .map((g) => {
      const v = store.state.financing.mission_cuts?.[g.id] ?? 0;
      const src = srcButton({ title: g.label, sources: [g.param.source], notes: [`Plafond : ${pct(g.max, 0)} des crédits du groupe.`, ...(g.param.note ? [g.param.note] : [])] }, { inline: true });
      return `<div class="lever">
        <div><div class="n"><label for="mc-${g.id}">${e(g.label)}</label>${src}</div><div class="s" id="mcprev-${g.id}">${off ? "" : missionPreviewHtml(store, g)}</div></div>
        <div class="val" id="vm-${g.id}">${v > 0 ? "−" : ""}${num(v, 0)} %</div>
        <input id="mc-${g.id}" type="range" min="0" max="${g.max}" step="1" value="${v}" data-mcut="${g.id}" ${off ? "disabled" : ""} aria-valuetext="${num(v, 0)} pour cent d'économies">
      </div>`;
    })
    .join("");
  return `<h3>Économies ciblées par mission</h3>${off ? `<p class="hint warn">Le moteur chargé n'applique pas encore ces économies : curseurs affichés pour information.</p>` : `<p class="hint">Fais glisser : part des crédits du groupe économisée chaque année ; l'aperçu montre ce que deviennent les besoins couverts.</p>`}${rows}`;
}

export function renderFinancement(store: Store): string {
  const p = preview(store);
  const st = store.state;
  // Bornes des curseurs : params `lev_<id>_min_bn` / `lev_<id>_max_bn` (source de vérité, ADR 0004 §4) ; rabot : `cut_max_share`.
  const cutMaxShare = store.content.params.cut_max_share;
  const rabotMax = cutMaxShare?.value != null ? cutMaxShare.value * 100 : undefined;
  const levers = store.content.levers
    .map((lv) => {
      const v = st.financing.revenue_raises[lv.id] ?? 0;
      // Bornes arrondies vers l'intérieur (curseur au pas de 1 Md€) pour rester dans les bornes du moteur.
      const max = Math.floor(lv.max.value ?? 0);
      const min = Math.ceil(Math.min(0, lv.min.value ?? 0));
      const step = lv.step ?? 1;
      const src = srcButton(
        {
          title: lv.label,
          sources: lv.sources.length ? lv.sources : ["TODO"],
          status: lv.status,
          notes: [`Bornes ${bn(min, 0)} à ${bn(max, 0)} (params lev_${lv.id}_min_bn / _max_bn) · fuite ${lv.leak.value !== null ? pct(lv.leak.value * 100, 1) : "—"} par Md€ · coût politique ${lv.political_cost_per_bn.value !== null ? num(lv.political_cost_per_bn.value, 2) : "—"} par Md€.`],
        },
        { inline: true },
      );
      return `<div class="lever">
        <div><div class="n"><label for="lv-${lv.id}">${e(lv.label)}</label> <span class="badge ${lv.status === "sourced" ? "ok" : ""}">${STATUS_LABELS[lv.status]}</span>${src}</div><div class="s">${e(lv.hint ?? "")}${min < 0 ? `${lv.hint ? " · " : ""}à gauche de 0 : baisse d'impôt` : ""}</div><div class="s" id="lvpol-${lv.id}">${leverPolHtml(store, lv.id)}</div></div>
        <div class="val" id="vf-${lv.id}">${signed(v, 0)} Md€</div>
        <input id="lv-${lv.id}" type="range" min="${min}" max="${max}" step="${step}" value="${v}" data-lever="${lv.id}" aria-valuetext="${signed(v, 0)} milliards d'euros">
      </div>`;
    })
    .join("");
  const rabotPct = st.financing.across_the_board_cut * 100;
  const rabot =
    rabotMax !== undefined
      ? `<div class="lever">
      <div><div class="n"><label for="lv-rabot">Rabot sur toutes les missions</label>${srcButton({ title: "Rabot transversal", sources: [cutMaxShare?.source ?? "TODO"], notes: [`≈ ${bn(p.rabot_bn_per_pt)} économisés par % de rabot la première année (part des crédits tendanciels des missions) ; rendement décroissant et perte d'indice de service paramétrés (cut_*).`] }, { inline: true })}</div><div class="s">≈ ${bn(p.rabot_bn_per_pt)} par %, dégrade l'indice de service public de chaque mission</div><div class="s" id="rabotpol">${rabotPolHtml(store, p.rabot_bn_per_pt)}</div></div>
      <div class="val" id="vf-rabot">${signed(rabotPct, 0)} %</div>
      <input id="lv-rabot" type="range" min="0" max="${rabotMax}" step="1" value="${rabotPct}" data-lever="rabot" aria-valuetext="${signed(rabotPct, 0)} pour cent">
    </div>`
      : `<p class="hint">Rabot indisponible : paramètre « cut_max_share » absent.</p>`;
  return `<div class="card">
    ${stepsHtml(store, 2)}
    <h1>Financer ton programme</h1>
    <div id="gapbox">${gapHtml(store, p)}</div>
    <div id="pricebox">${priceHtml(store)}</div>
  </div>
  <div class="two">
  <div class="card"><h2>Prélèvements</h2><p class="hint">Fais glisser : montant brut affiché ; la simulation applique la fuite et la réaction de l'économie. Chaque curseur affiche son coût politique, dans la même unité que les économies.</p>${levers}</div>
  <div class="card"><h2>Économies</h2>${missionCutsBlock(store)}<h3>Rabot sur toutes les missions</h3>${rabot}
    <p class="mut sm">D'autres économies structurelles (fonctionnaires, retraites, chômage…) sont des mesures du catalogue.</p></div>
  </div>
  <div class="card"><h2>Effet affiché vs effet après réaction</h2><p class="mut sm">À gauche, l'arithmétique d'un tract. À droite, la simulation une fois que croissance, fuite, taux et confiance ont réagi.</p><div id="cmpbox">${cmpHtml(p)}</div></div>
  <div class="card"><h2>Le compte de la première année (${p.first_year.year})</h2><div id="ledgerbox">${ledgerHtml(p.first_year, store.engine.name)}</div></div>
  <div class="card"><p class="mut sm">Une fois le budget voté, programme et financement sont verrouillés pour le mandat : tes seules décisions seront les réponses à une dizaine de crises, chacune avec son coût et son financement.</p><button type="button" class="sec" data-action="go-programme">Revenir au programme</button></div>`;
}

export function renderFinancementBottom(store: Store): string {
  const p = preview(store);
  const cap0 = store.content.baseline.politics.political_capital?.value ?? 0;
  const capAfter = cap0 - p.political_cost_programme - p.political_cost_per_year;
  const warn = capAfter < 0;
  return `<div class="in">
    <div class="nums">
      <div>Déficit ${p.first_year.year}<b>${pct(p.deficit_simulated_pct)}</b></div>
      <div>Dette<b>${pct(p.debt_simulated_pct, 0)}</b></div>
      ${p.first_year.kpis.parliament_majority !== undefined ? `<div>Majorité<b class="${p.first_year.kpis.parliament_majority > majorityThreshold(store) ? "" : "down"}">${num(p.first_year.kpis.parliament_majority, 0)} %</b></div>` : `<div>Capital pol.<b class="${warn ? "down" : ""}">${num(capAfter, 0)}</b></div>`}
    </div>
    <button type="button" data-action="vote" class="primary${warn ? " danger" : ""}">${warn ? "Voter (censure probable)" : "Voter le budget"}</button>
  </div>`;
}
